import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import useAbortController from "../../../Common/hooks/useAbortController";
import { getPresignedURL } from "../../../../utils/helpers";
import ConfirmationModal from "../../../Common/ConfirmationModal";
import PETRIFILM_COLOR_MAP, { resolvePetrifilmEntry } from "./utils";
import AnnotateCanvasStage from "./AnnotateCanvasStage";
import AnnotateBoxToolsPanel from "./AnnotateBoxToolsPanel";

const IMAGE_SIZE = 1320;
const NEW_BOX_SIZE = 120;

const buildBoxesFromColonyList = (colonyBoxList = [], labels = [], petrifilmColorConfig = {}, petrifilmType = "") => colonyBoxList.map((item, index) => {
  const [x1, y1, x2, y2] = item;
  const x = Math.min(x1, x2);
  const y = Math.min(y1, y2);
  const width = Math.abs(x2 - x1);
  const height = Math.abs(y2 - y1);

  const labelValue = labels[index];
  const raw = petrifilmColorConfig[labelValue] ?? petrifilmColorConfig[String(labelValue)];
  const fallbackText = `${petrifilmType}-${String(labelValue ?? "")}`;
  const resolved = resolvePetrifilmEntry(raw, fallbackText);

  return {
    id: `box-${index}`,
    x,
    y,
    width,
    height,
    customLabel: labelValue ?? null,
    customColor: raw ? resolved.color : undefined,
  };
});

const boxToColonyBox = (box) => {
  const x1 = Math.round(box.x);
  const y1 = Math.round(box.y);
  const x2 = Math.round(box.x + box.width);
  const y2 = Math.round(box.y + box.height);
  return [x1, y1, x2, y2];
};

const cloneBoxes = (nextBoxes) => nextBoxes.map((box) => ({ ...box }));

const AnnotateKonvaCanvas = forwardRef(({
  annotationData,
  originalAnnotationData,
  interactionMode = "qc",
  cancelDraftTrigger,
  saveDraftTrigger,
  onDraftBoxCreated,
  onImageLoadingChange,
  onImageLoadError,
  annotationPath,
}, ref) => {
  const imageRef = useRef(null);
  const canvasStageRef = useRef(null);
  const { abortController, setNewAbortController } = useAbortController();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [boxes, setBoxes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const selectedIdRef = useRef(null);
  const [draftBoxId, setDraftBoxId] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const workingBoxesRef = useRef([]);
  const originalBoxesRef = useRef([]);
  const qcSelectedIdRef = useRef(null);
  const interactionModeRef = useRef(interactionMode);
  const annotationSignatureRef = useRef("");
  const interactionLocked = isImageLoading;

  useEffect(() => {
    if (typeof onImageLoadingChange === "function") {
      onImageLoadingChange(isImageLoading);
    }
  }, [isImageLoading, onImageLoadingChange]);

  const reportImageLoadError = useCallback((message) => {
    onImageLoadError(message || "Failed to load image");
  }, [onImageLoadError]);

  useEffect(() => {
    interactionModeRef.current = interactionMode;
  }, [interactionMode]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const annotationMeta = useMemo(() => {
    const colonyBoxList = annotationData?.colony_box_list || [];
    const labels = annotationData?.labels || [];
    const petrifilmType = annotationData?.petrifilm || "";
    const petrifilmColorConfig = PETRIFILM_COLOR_MAP[petrifilmType] || {};
    const roiPath = annotationData?.roi_path || "";
    const pathParts = roiPath.split("/");
    const roiImageName = pathParts[pathParts.length - 1] || "annotatable-image";

    return {
      colonyBoxList,
      labels,
      petrifilmType,
      petrifilmColorConfig,
      roiImageName,
    };
  }, [annotationData]);

  const {
    colonyBoxList,
    labels,
    petrifilmType,
    petrifilmColorConfig,
    roiImageName,
  } = annotationMeta;

  const originalMeta = useMemo(() => {
    const colonyBoxListOriginal = originalAnnotationData?.colony_box_list || [];
    const labelsOriginal = originalAnnotationData?.labels || [];
    return { colonyBoxListOriginal, labelsOriginal };
  }, [originalAnnotationData]);

  const colorOptions = useMemo(
    () => Object.entries(petrifilmColorConfig).map(([key, entry]) => {
      const parsedKey = Number.isNaN(Number(key)) ? key : Number(key);
      const fallbackText = `${petrifilmType}-${key}`;
      const { color, label: displayName } = resolvePetrifilmEntry(entry, fallbackText);
      return {
        key,
        value: parsedKey,
        color,
        displayName,
        label: `${key}: ${displayName}`,
      };
    }),
    [petrifilmColorConfig, petrifilmType],
  );

  useEffect(() => {
    const built = buildBoxesFromColonyList(colonyBoxList, labels, petrifilmColorConfig, petrifilmType);
    const nextSignature = JSON.stringify({
      colonyBoxList,
      labels,
      petrifilmType,
      petrifilmKeys: Object.keys(petrifilmColorConfig || {}).sort(),
    });

    const signatureChanged = annotationSignatureRef.current !== nextSignature;
    annotationSignatureRef.current = nextSignature;

    if (signatureChanged || workingBoxesRef.current.length === 0) {
      workingBoxesRef.current = cloneBoxes(built);
    }

    if (interactionModeRef.current !== "original") {
      setBoxes(cloneBoxes(workingBoxesRef.current));
    }

    setSelectedId((current) => {
      const nextBoxes = workingBoxesRef.current;
      if (current && nextBoxes.some((b) => b.id === current)) return current;
      return null;
    });
  }, [colonyBoxList, labels, petrifilmColorConfig, petrifilmType]);

  useEffect(() => {
    const builtOriginal = buildBoxesFromColonyList(
      originalMeta.colonyBoxListOriginal,
      originalMeta.labelsOriginal,
      petrifilmColorConfig,
      petrifilmType,
    );
    originalBoxesRef.current = cloneBoxes(builtOriginal);
    if (interactionModeRef.current === "original") {
      setBoxes(cloneBoxes(originalBoxesRef.current));
      setSelectedId(null);
    }
  }, [originalMeta.colonyBoxListOriginal, originalMeta.labelsOriginal, petrifilmColorConfig, petrifilmType]);

  useEffect(() => {
    if (interactionMode === "original") {
      qcSelectedIdRef.current = selectedIdRef.current;
      setBoxes(cloneBoxes(originalBoxesRef.current));
      return;
    }

    setBoxes(cloneBoxes(workingBoxesRef.current));
    setSelectedId((current) => {
      if (current) return current;
      const restored = qcSelectedIdRef.current;
      if (restored && workingBoxesRef.current.some((b) => b.id === restored)) {
        return restored;
      }
      return null;
    });
  }, [interactionMode]);

  useEffect(() => {
    if (interactionMode !== "qc") return;
    workingBoxesRef.current = cloneBoxes(boxes);
  }, [boxes, interactionMode]);

  const selectedBox = useMemo(
    () => boxes.find((box) => box.id === selectedId) || null,
    [boxes, selectedId],
  );

  useEffect(() => {
    if (interactionMode !== "qc") return;
    if (!cancelDraftTrigger || !draftBoxId) {
      return;
    }
    setBoxes((prev) => prev.filter((box) => box.id !== draftBoxId));
    if (selectedId === draftBoxId) {
      setSelectedId(null);
    }
    setDraftBoxId(null);
  }, [cancelDraftTrigger, draftBoxId, interactionMode, selectedId]);

  useEffect(() => {
    if (interactionMode !== "qc") return;
    if (!saveDraftTrigger || !draftBoxId) {
      return;
    }
    setDraftBoxId(null);
  }, [saveDraftTrigger, draftBoxId, interactionMode]);

  useEffect(() => {
    const roiPath = annotationData?.roi_path;
    if (!roiPath) {
      imageRef.current = null;
      setIsImageLoading(false);
      return undefined;
    }

    let mounted = true;

    const fetchImageSrc = async () => {
      setIsImageLoading(true);
      const newAbortController = setNewAbortController();

      const response = await getPresignedURL({
        action: "get",
        urls: [{ ...annotationPath, Name: roiPath }],
      }, newAbortController.signal);

      if (response.aborted) {
        if (mounted) {
          setIsImageLoading(false);
        }
        return;
      }

      if (response.success && response.result && Object.keys(response.result).length > 0) {
        const imageUrl = response.result[roiPath];
        if (!imageUrl) {
          if (mounted) {
            setIsImageLoading(false);
          }
          toast.error("Failed to load image from S3");
          reportImageLoadError("Failed to load image from S3");
          return;
        }

        const image = new window.Image();
        image.src = imageUrl;
        image.onload = () => {
          if (mounted) {
            imageRef.current = image;
            setIsImageLoading(false);
          }
        };
        image.onerror = () => {
          if (mounted) {
            setIsImageLoading(false);
            toast.error("Failed to load image");
            reportImageLoadError("Failed to load image");
          }
        };
      } else {
        if (mounted) {
          setIsImageLoading(false);
        }
        const message = response.message || "Failed to fetch the image";
        toast.error(message);
        reportImageLoadError(message);
      }
    };

    fetchImageSrc();

    return () => {
      mounted = false;
      if (abortController.current) {
        abortController.current.abort(); // eslint-disable-line
      }
    };
  }, [annotationData?.roi_path, annotationPath]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartAdd = useCallback(() => {
    if (interactionLocked || interactionMode !== "qc") return;
    if (!colorOptions.length) return;

    const optionToUse = colorOptions[0];
    const size = NEW_BOX_SIZE;
    const center = canvasStageRef.current?.getViewportCenterInImageCoords?.();
    const rawX = (center?.x ?? IMAGE_SIZE / 2) - (size / 2);
    const rawY = (center?.y ?? IMAGE_SIZE / 2) - (size / 2);
    const x = Math.max(0, Math.min(rawX, IMAGE_SIZE - size));
    const y = Math.max(0, Math.min(rawY, IMAGE_SIZE - size));
    const newBoxId = `box-${Date.now()}`;

    setBoxes((prev) => [...prev, {
      id: newBoxId,
      x,
      y,
      width: size,
      height: size,
      customColor: optionToUse?.color || "#ff0000",
      customLabel: optionToUse?.value ?? null,
    }]);
    setDraftBoxId(newBoxId);
    setSelectedId(newBoxId);

    if (typeof onDraftBoxCreated === "function") {
      onDraftBoxCreated(newBoxId);
    }
  }, [colorOptions, interactionLocked, interactionMode, onDraftBoxCreated]);

  const handleUpdateSelectedBoxLabel = useCallback((labelKey) => {
    if (interactionLocked || interactionMode !== "qc") return;
    if (!selectedId) return;

    const optionToUse = colorOptions.find((opt) => opt.key === labelKey);
    if (!optionToUse) return;

    setBoxes((prev) => prev.map((box) => (box.id === selectedId ? {
      ...box,
      customColor: optionToUse.color,
      customLabel: optionToUse.value ?? null,
    } : box)));
  }, [colorOptions, interactionLocked, interactionMode, selectedId]);

  const handleDeleteBoxById = useCallback((id) => {
    if (interactionLocked || interactionMode !== "qc") return;
    if (!id) return;

    setBoxes((prev) => prev.filter((box) => box.id !== id));
    if (draftBoxId === id) {
      setDraftBoxId(null);
    }
    if (selectedId === id) {
      setSelectedId(null);
    }
  }, [draftBoxId, interactionLocked, interactionMode, selectedId]);

  const requestDeleteBox = useCallback((id) => {
    if (interactionLocked || interactionMode !== "qc") return;
    if (!id) return;
    setPendingDeleteId(id);
    setConfirmDeleteOpen(true);
  }, [interactionLocked, interactionMode]);

  const handleConfirmDeleteBox = useCallback(() => {
    if (pendingDeleteId == null) return;
    handleDeleteBoxById(pendingDeleteId);
    setConfirmDeleteOpen(false);
    setPendingDeleteId(null);
  }, [handleDeleteBoxById, pendingDeleteId]);

  const handleCancelDeleteBox = useCallback(() => {
    setConfirmDeleteOpen(false);
    setPendingDeleteId(null);
  }, []);

  useEffect(() => {
    if (interactionMode !== "qc" || interactionLocked || !selectedId) {
      return undefined;
    }

    const isEditableTarget = (target) => {
      if (!target || !target.tagName) return false;
      const tag = target.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (target.isContentEditable) return true;
      return false;
    };

    const onKeyDown = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      requestDeleteBox(selectedId);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [interactionLocked, interactionMode, requestDeleteBox, selectedId]);

  useImperativeHandle(ref, () => ({
    getColonyEdits: () => {
      const sourceBoxes = interactionMode === "qc" ? boxes : workingBoxesRef.current;
      const colony_box_list = sourceBoxes.map(boxToColonyBox);
      const nextLabels = sourceBoxes.map((box) => box.customLabel);

      return { colony_box_list, labels: nextLabels };
    },
  }), [boxes, interactionMode]);

  return (
    <div className="AnnotateImageModal__Panels">
      <div className="AnnotateImageModal__LeftPanel">
        <AnnotateCanvasStage
          ref={canvasStageRef}
          imageSize={IMAGE_SIZE}
          imageRef={imageRef}
          isImageLoading={isImageLoading}
          roiImageName={roiImageName}
          boxes={boxes}
          setBoxes={setBoxes}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          labels={labels}
          petrifilmColorConfig={petrifilmColorConfig}
          readOnly={interactionLocked || interactionMode !== "qc"}
        />
      </div>

      <AnnotateBoxToolsPanel
        boxes={boxes}
        colorOptions={colorOptions}
        originalColonyBoxList={originalMeta.colonyBoxListOriginal}
        originalLabels={originalMeta.labelsOriginal}
        selectedId={selectedId}
        isEditMode={Boolean(selectedId)}
        selectedBox={selectedBox}
        readOnly={interactionLocked || interactionMode !== "qc"}
        onAddBox={handleStartAdd}
        onSelectLabel={handleUpdateSelectedBoxLabel}
        onSelectBox={setSelectedId}
        onDeleteBox={requestDeleteBox}
      />

      <ConfirmationModal
        title="Delete box"
        bodyText="Are you sure you want to delete selected box?"
        open={confirmDeleteOpen}
        okText="Yes"
        cancelText="No"
        onOk={handleConfirmDeleteBox}
        onCancel={handleCancelDeleteBox}
      />
    </div>
  );
});

export default AnnotateKonvaCanvas;
